import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import CertificateBlock from "../components/CertificateBlock";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, PlayCircle, CheckCircle2, Lock, ClipboardList, Trophy, Loader2,
  ChevronRight, ChevronDown, AlertTriangle, Play, VideoOff,
} from "lucide-react";

export default function StudenteCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  // Moduli espansi (accordion). null = stato di default: aperto solo il modulo
  // in corso; appena l'utente tocca un modulo si passa a un set esplicito.
  const [openModules, setOpenModules] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/courses/${id}/progress`);
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/studente");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const startTest = async (path) => {
    setStarting(true);
    try {
      const { data } = await api.post(path);
      navigate(`/studente/test/${data.attempt_id}`, { state: { start: data } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        toast.error(detail.message || detail.code || formatApiError(err));
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setStarting(false);
    }
  };

  if (loading || !summary) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="text-center py-16 text-brand-muted">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
        </div>
      </div>
    );
  }

  // Accordion: di default è aperto il primo modulo non completato (quello su cui
  // lo studente sta lavorando); se sono tutti completati, il primo.
  const defaultOpenId = (summary.modules.find((m) => !m.module_completed) || summary.modules[0])?.id;
  const isModuleOpen = (mid) => (openModules ? openModules.has(mid) : mid === defaultOpenId);
  const toggleModule = (mid) =>
    setOpenModules((prev) => {
      const next = new Set(prev ?? (defaultOpenId ? [defaultOpenId] : []));
      next.has(mid) ? next.delete(mid) : next.add(mid);
      return next;
    });

  return (
    <div data-testid="student-course-detail" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate("/studente")}
          data-testid="back-to-courses"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Torna all'area studente
        </button>

        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">Corso</div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">{summary.title}</h1>
          <p className="text-brand-muted max-w-3xl">{summary.description}</p>
          <div className="mt-4 max-w-md">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-brand-muted">Progresso corso</span>
              <span className="font-semibold text-brand-blue" data-testid="course-progress-pct">{summary.course_percent_completed}%</span>
            </div>
            <div className="h-1.5 bg-brand-line rounded-sm overflow-hidden">
              <div className="h-full bg-brand-blue" style={{ width: `${summary.course_percent_completed}%` }} />
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-10">
          {summary.modules.map((m, i) => (
            <div key={m.id} data-testid={`student-module-${m.id}`} className="bg-white border border-brand-line rounded-sm overflow-hidden">
              {(() => {
                const total = m.lessons.length;
                const done = m.lessons.filter((l) => l.is_completed).length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                const open = isModuleOpen(m.id);
                return (
                  <button
                    type="button"
                    onClick={() => toggleModule(m.id)}
                    data-testid={`module-header-${m.id}`}
                    aria-expanded={open}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-brand-bg/60 transition-colors"
                  >
                    <span
                      className={
                        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-sm font-semibold " +
                        (m.module_completed
                          ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
                          : "bg-brand-blue/10 text-brand-blue")
                      }
                    >
                      {m.module_completed ? <CheckCircle2 size={18} /> : i + 1}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-[10px] uppercase tracking-widest text-brand-gold">Modulo {i + 1}</span>
                      <span className="block text-base font-semibold text-brand-ink truncate">{m.title}</span>
                      <span className="mt-1.5 flex items-center gap-2">
                        <span className="h-1.5 flex-1 max-w-[220px] bg-brand-line rounded-sm overflow-hidden">
                          <span
                            className={"block h-full " + (m.module_completed ? "bg-emerald-500" : "bg-brand-blue")}
                            style={{ width: `${pct}%` }}
                          />
                        </span>
                        <span className="text-[11px] text-brand-muted shrink-0">{done}/{total} lezioni</span>
                      </span>
                    </span>

                    {m.has_intermediate_test &&
                      (m.intermediate_passed ? (
                        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-0.5 shrink-0">
                          <CheckCircle2 size={12} /> Test superato
                        </span>
                      ) : m.intermediate_test_unlocked ? (
                        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-brand-blue bg-brand-blue/10 rounded-sm px-2 py-0.5 shrink-0">
                          <ClipboardList size={12} /> Test pronto
                        </span>
                      ) : (
                        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-brand-muted shrink-0">
                          <Lock size={12} /> Test bloccato
                        </span>
                      ))}

                    <ChevronDown
                      size={18}
                      className={"text-brand-muted shrink-0 transition-transform duration-200 " + (open ? "rotate-180" : "")}
                    />
                  </button>
                );
              })()}

              {isModuleOpen(m.id) && (
                <div className="border-t border-brand-line">
                  <div className="p-3 space-y-1.5">
                {m.lessons.map((l) => {
                  const content = (
                    <>
                      <div className="flex items-center gap-3 min-w-0">
                        {l.is_locked ? (
                          <Lock size={16} className="text-brand-muted shrink-0" />
                        ) : l.is_completed ? (
                          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                        ) : l.has_video === false ? (
                          <VideoOff size={16} className="text-brand-muted shrink-0" />
                        ) : (
                          <PlayCircle size={16} className="text-brand-blue shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className={"text-sm font-semibold truncate " + (l.is_locked ? "text-brand-muted" : "text-brand-ink")}>
                            {l.title}
                          </div>
                          <div className="text-xs text-brand-muted">
                            {l.is_locked
                              ? l.lock_reason || "Completa le lezioni precedenti per sbloccarla"
                              : l.is_completed
                              ? "Completata"
                              : l.has_video === false
                              ? "Video non ancora disponibile"
                              : `Visto ${Math.round(l.percent_watched)}%`}
                          </div>
                        </div>
                      </div>
                      {!l.is_locked && <ChevronRight size={16} className="text-brand-blue shrink-0" />}
                    </>
                  );

                  // Bloccata: nessun collegamento, così non è raggiungibile per errore.
                  // Il backend rifiuta comunque la richiesta, questo evita il vicolo cieco.
                  return l.is_locked ? (
                    <div
                      key={l.id}
                      data-testid={`student-lesson-locked-${l.id}`}
                      className="flex items-center justify-between gap-3 border border-dashed border-brand-line rounded-sm px-3 py-2 bg-brand-bg cursor-not-allowed"
                    >
                      {content}
                    </div>
                  ) : (
                    <Link
                      to={`/studente/corsi/${id}/lezioni/${l.id}`}
                      key={l.id}
                      data-testid={`student-lesson-${l.id}`}
                      className="flex items-center justify-between gap-3 border border-brand-line rounded-sm px-3 py-2 hover:border-brand-blue/40 transition-colors bg-white"
                    >
                      {content}
                    </Link>
                  );
                })}
              </div>

              {/* Intermediate test row */}
              {m.has_intermediate_test && (
                <div className="px-4 py-3 border-t border-brand-line bg-brand-bg">
                  {m.intermediate_locked ? (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3" data-testid={`intermediate-locked-${m.id}`}>
                      <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-red-800">
                        <strong>Hai esaurito i 3 tentativi</strong> di questo test intermedio.
                        Rivedi tutte le lezioni del modulo per sbloccare nuovamente il test.
                      </div>
                    </div>
                  ) : m.intermediate_passed ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold">
                        <CheckCircle2 size={16} className="text-emerald-600" /> Test intermedio superato
                        <span className="text-xs text-brand-muted font-normal">
                          ({m.intermediate_attempts_used}/{m.intermediate_attempts_max} tentativi usati)
                        </span>
                      </div>
                      <button
                        onClick={() => startTest(`/tests/intermediate/${m.id}/start`)}
                        disabled={starting || m.intermediate_attempts_used >= m.intermediate_attempts_max}
                        data-testid={`start-intermediate-${m.id}`}
                        className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 font-semibold text-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Riprova
                      </button>
                    </div>
                  ) : m.intermediate_test_unlocked ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-brand-muted">
                        <ClipboardList size={14} className="inline mr-1 text-brand-blue" />
                        Test intermedio disponibile — Tentativi: <strong>{m.intermediate_attempts_used}/{m.intermediate_attempts_max}</strong>
                      </div>
                      <button
                        onClick={() => startTest(`/tests/intermediate/${m.id}/start`)}
                        disabled={starting}
                        data-testid={`start-intermediate-${m.id}`}
                        className="btn-primary inline-flex items-center gap-1.5"
                      >
                        <ClipboardList size={14} /> Fai test intermedio
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-brand-muted" data-testid={`intermediate-blocked-${m.id}`}>
                      <Lock size={14} /> <strong>Test intermedio bloccato</strong> — completa tutte le lezioni del modulo per sbloccarlo.
                    </div>
                  )}
                </div>
              )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Final test */}
        {summary.has_final_test && (
          <div
            data-testid="final-test-block"
            className={
              "rounded-sm p-6 sm:p-8 mb-10 " +
              (summary.final_test_unlocked ? "bg-brand-blue text-white" : "bg-white border border-brand-line")
            }
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className={"flex items-center gap-2 text-xs font-semibold uppercase tracking-widest mb-2 " + (summary.final_test_unlocked ? "text-brand-bg" : "text-brand-muted")}>
                  <Trophy size={14} /> Test finale del corso
                </div>
                <h2 className={"text-2xl mb-1 " + (summary.final_test_unlocked ? "" : "text-brand-ink")}>Verifica finale</h2>
                {summary.final_test_unlocked ? (
                  <p className="text-white/80 text-sm max-w-lg">
                    {summary.final_test_passed
                      ? "Hai già superato il test finale. Puoi ripeterlo per migliorare il punteggio."
                      : "Tentativi illimitati. Le domande vengono estratte casualmente ad ogni prova."}
                  </p>
                ) : (
                  <p className="text-brand-muted text-sm max-w-lg flex items-center gap-2">
                    <Lock size={14} /> Sblocca il test finale superando tutti i test intermedi del corso.
                  </p>
                )}
              </div>
              {summary.final_test_unlocked ? (
                <button
                  onClick={() => startTest(`/tests/final/${id}/start`)}
                  disabled={starting}
                  data-testid="start-final-test-btn"
                  className="rounded-sm bg-brand-gold hover:shadow-md text-brand-blue-dark font-semibold px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60 transition-shadow"
                >
                  {starting && <Loader2 size={14} className="animate-spin" />}
                  <Play size={14} /> Inizia test finale
                </button>
              ) : (
                <button disabled data-testid="final-test-locked-btn" className="rounded-sm bg-brand-line text-gray-400 font-semibold px-5 py-2.5 inline-flex items-center gap-2 cursor-not-allowed">
                  <Lock size={14} /> Bloccato
                </button>
              )}
            </div>
          </div>
        )}

        {/* Certificate block (appears when final test passed) */}
        <div className="mb-10">
          <CertificateBlock courseId={id} finalPassed={summary.final_test_passed} />
        </div>
      </main>
    </div>
  );
}
